import {DEFAULT_FAVORITES_PARAM} from '@shared/utils/legacySignalsRedirect'

// One builder for every in-app link into the Signal Explorer (AB#5977).
//
// Before the per-line signals page was retired, these links spoke ITS dialect
// (?equipment=, ?signal=, ?program_id=, ?batch_mode=) and leaned on the route
// guard to translate. The guard now only serves URLs arriving from outside —
// emails, bookmarks — so anything the app builds itself says what it means.
//
// Param names are the workspace's own (useSignalViewConfig.PAGE_PARAMS); core
// builds the same shape for alert links (api/utils/signal_explorer_links.py).

export const SIGNAL_EXPLORER_PATH = '/signal-explorer'

// How the explorer frames a program's runs: 'window' takes whatever ran in the
// time window, 'lastN:<n>' the last n runs, 'selection' exactly the batches
// named in batchIds — window-independent, so a run outside the current window
// still resolves.
export type ProgramRule = 'window' | 'selection' | `lastN:${number}`

export type SignalExplorerTarget = {
  // Hierarchy filter. lineId is the machine, equipmentId the sub-system.
  lineId?: string | null
  equipmentId?: string | null
  productionAreaId?: string | null
  // Plotted signals, one chart each. Empty + a hierarchy filter means "seed the
  // favourites" (defaultFavorites), which is what the retired page showed on
  // arrival — an empty workspace would read as a broken link.
  signalIds?: readonly string[]
  // Explicit window. Both ends or neither; a half-open one keeps the page's
  // rolling default rather than inventing an end.
  startDate?: Date | string | null
  endDate?: Date | string | null
  programId?: string | null
  rule?: ProgramRule
  // Hand-picked runs, for rule: 'selection'.
  batchIds?: readonly string[]
  // Overlay the program's runs on elapsed time instead of shading them.
  overlay?: boolean
}

const iso = (value: Date | string): string =>
  value instanceof Date ? value.toISOString() : new Date(value).toISOString()

export const buildSignalExplorerUrl = ({
  lineId,
  equipmentId,
  productionAreaId,
  signalIds = [],
  startDate,
  endDate,
  programId,
  rule,
  batchIds = [],
  overlay,
}: SignalExplorerTarget): string => {
  const params = new URLSearchParams()
  if (productionAreaId) {
    params.set('productionAreaId', productionAreaId)
  }
  if (lineId) {
    params.set('lineId', lineId)
  }
  if (equipmentId) {
    params.set('equipmentId', equipmentId)
  }
  if (signalIds.length) {
    // Comma separates charts; a dot would stack the signals into one.
    params.set('sig', signalIds.join(','))
  } else if (lineId || equipmentId) {
    params.set(DEFAULT_FAVORITES_PARAM, '1')
  }
  if (startDate && endDate) {
    params.set('start_date', iso(startDate))
    params.set('end_date', iso(endDate))
    // An explicit window, not a rolling range.
    params.set('time_range', 'null')
  }
  if (programId) {
    params.set('prog', programId)
    params.set('rule', rule ?? 'window')
    if (batchIds.length) {
      params.set('bi', batchIds.join(','))
    }
    if (overlay) {
      params.set('overlay', '1')
    }
  }
  const query = params.toString()
  return query ? `${SIGNAL_EXPLORER_PATH}?${query}` : SIGNAL_EXPLORER_PATH
}
